import type { Discount, PricingItem, QuotePricingInput, QuoteTotals } from "./types.ts";

const ZERO_TOTALS: QuoteTotals = {
  subtotalMinor: 0,
  taxableSubtotalMinor: 0,
  discountMinor: 0,
  subtotalBasisMinor: 0,
  taxableBasisMinor: 0,
  taxAmountMinor: 0,
  grandTotalMinor: 0,
};

export function getLineAmountMinor(item: PricingItem): number {
  if (typeof item.amountMinor === "number") {
    return roundMoney(item.amountMinor);
  }

  const quantity = item.quantity ?? 0;
  const days = item.days ?? 1;
  const rateMinor = item.rateMinor ?? 0;

  return roundMoney(quantity * days * rateMinor);
}

export function shouldCountItem(item: PricingItem): boolean {
  if (item.visibleToClient === false || item.status === "hidden") {
    return false;
  }

  if (item.status === "normal") {
    return true;
  }

  if (item.status === "optional") {
    return item.includeInTotal === true;
  }

  return false;
}

export function calculateQuoteTotals(input: QuotePricingInput): QuoteTotals {
  if (input.items.length === 0) {
    return ZERO_TOTALS;
  }

  const includedItems = input.items.filter(shouldCountItem);
  const subtotalMinor = sumMinor(includedItems.map(getLineAmountMinor));
  const taxableSubtotalMinor = sumMinor(
    includedItems.filter((item) => item.taxable !== false).map(getLineAmountMinor),
  );
  const discountMinor = getDiscountMinor(input.discount, subtotalMinor);
  const subtotalBasisMinor = subtotalMinor;
  const taxableBasisMinor = taxableSubtotalMinor;
  const taxAmountMinor = getTaxAmountMinor(input.taxMode, taxableBasisMinor, input.taxRate);
  const grandTotalMinor = input.taxMode === "add_tax" ? subtotalBasisMinor + taxAmountMinor : subtotalBasisMinor;

  return {
    subtotalMinor,
    taxableSubtotalMinor,
    discountMinor,
    subtotalBasisMinor,
    taxableBasisMinor,
    taxAmountMinor,
    grandTotalMinor: roundMoney(grandTotalMinor),
  };
}

function getDiscountMinor(discount: Discount | undefined, subtotalMinor: number): number {
  if (!discount) {
    return 0;
  }

  if (discount.type === "fixed") {
    return Math.min(roundMoney(discount.valueMinor), subtotalMinor);
  }

  return Math.min(roundMoney(subtotalMinor * (discount.valuePercent / 100)), subtotalMinor);
}

function getTaxAmountMinor(taxMode: QuotePricingInput["taxMode"], taxableBasisMinor: number, taxRate: number): number {
  if (taxMode === "no_tax" || taxRate <= 0) {
    return 0;
  }

  if (taxMode === "tax_included") {
    return roundMoney(taxableBasisMinor * (taxRate / (1 + taxRate)));
  }

  return roundMoney(taxableBasisMinor * taxRate);
}

function sumMinor(values: number[]): number {
  return values.reduce((total, value) => total + roundMoney(value), 0);
}

function roundMoney(value: number): number {
  return Math.round(value);
}
